import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Sparkles, 
  Camera, 
  Cpu, 
  Layers, 
  Scale, 
  ShieldCheck, 
  Zap, 
  ArrowRight, 
  CheckCircle2, 
  HelpCircle, 
  Flame, 
  Utensils, 
  Beef, 
  Apple, 
  Carrot, 
  Wheat, 
  Droplets, 
  Eye, 
  Sliders, 
  ChevronDown, 
  ChevronUp, 
  Sun, 
  Moon, 
  Scan, 
  Activity, 
  Binary, 
  Info,
  ChevronRight,
  Lightbulb,
  Check,
  Award,
  Sparkle
} from 'lucide-react';
import { Language, TotalNutrition } from '../types';
import { triggerHaptic } from '../lib/haptic';
import { sounds } from '../lib/sound';
import { cn } from '../lib/utils';

interface HowItWorksPageProps {
  lang: Language;
  theme: string;
  skin?: string;
  onStartScan: () => void;
  onBack?: () => void;
}

// Sample meals for the Interactive AI Plate Inspector
interface SampleIngredient {
  id: string;
  nameEn: string;
  nameUa: string;
  weight_g: number;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  confidence: number;
  density: number; // g/cm^3
  methodEn: string;
  methodUa: string;
  category: 'meat' | 'fish' | 'grain' | 'vegetable' | 'dairy' | 'fat' | 'fruit';
  topPercent: number; // For visual HUD overlay positioning
  leftPercent: number;
}

interface SampleDish {
  id: string;
  titleEn: string;
  titleUa: string;
  emoji: string;
  taglineEn: string;
  taglineUa: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  ingredients: SampleIngredient[];
}

const SAMPLE_DISHES: SampleDish[] = [
  {
    id: 'salmon_bowl',
    titleEn: 'Wild Salmon Power Bowl',
    titleUa: 'Боул з диким лососем та кіноа',
    emoji: '🥗',
    taglineEn: 'Clean high-protein meal with omega-3 fats & slow complex carbs',
    taglineUa: 'Чистий високобілковий раціон з омега-3 та повільними вуглеводами',
    totalCalories: 645,
    totalProtein: 44,
    totalCarbs: 48,
    totalFat: 29,
    ingredients: [
      {
        id: 'salmon',
        nameEn: 'Pan-Seared Salmon Fillet',
        nameUa: 'Філе дикого лосося (обсмажене)',
        weight_g: 180,
        calories: 360,
        protein_g: 36,
        carbs_g: 0,
        fat_g: 22,
        confidence: 98.4,
        density: 1.05,
        methodEn: 'Pan-seared in olive oil. High protein, rich in EPA/DHA Omega-3.',
        methodUa: 'Обсмажений на оливковій олії. Високий білок, багатий на EPA/DHA Омега-3.',
        category: 'fish',
        topPercent: 55,
        leftPercent: 32
      },
      {
        id: 'avocado',
        nameEn: 'Fresh Hass Avocado Slices',
        nameUa: 'Свіжий авокадо Хасс',
        weight_g: 65,
        calories: 104,
        protein_g: 1.3,
        carbs_g: 5.5,
        fat_g: 9.8,
        confidence: 96.1,
        density: 0.96,
        methodEn: 'Raw sliced Hass avocado. Monounsaturated oleic acid & dietary fiber.',
        methodUa: 'Свіжі скибочки авокадо. Мононенасичені жири та корисна клітковина.',
        category: 'fat',
        topPercent: 42,
        leftPercent: 43
      },
      {
        id: 'quinoa',
        nameEn: 'Fluffy Cooked Quinoa',
        nameUa: 'Відварене кіноа з зеленню',
        weight_g: 130,
        calories: 156,
        protein_g: 5.7,
        carbs_g: 28,
        fat_g: 2.5,
        confidence: 94.7,
        density: 0.78,
        methodEn: 'Steamed ancient grain. Complete amino acid profile with slow glycemic index.',
        methodUa: 'Приготована на пару крупа. Повний амінокислотний профіль, низький ГІ.',
        category: 'grain',
        topPercent: 46,
        leftPercent: 62
      },
      {
        id: 'asparagus',
        nameEn: 'Grilled Asparagus Spears',
        nameUa: 'Спаржа гриль з лимоном',
        weight_g: 80,
        calories: 25,
        protein_g: 1.8,
        carbs_g: 3.2,
        fat_g: 0.4,
        confidence: 97.8,
        density: 0.52,
        methodEn: 'Char-grilled with lemon zest. Rich in prebiotic inulin, folate & potassium.',
        methodUa: 'Запечена на грилі. Джерело пребіотичного інуліну, фолату та калію.',
        category: 'vegetable',
        topPercent: 68,
        leftPercent: 65
      }
    ]
  },
  {
    id: 'ribeye_steak',
    titleEn: 'Dry-Aged Ribeye & Greens',
    titleUa: 'Рібай стейк з броколі та бататом',
    emoji: '🥩',
    taglineEn: 'Anabolic powerhouse meal for maximum muscle protein synthesis',
    taglineUa: 'Анаболічний заряд для максимального синтезу м’язового білка',
    totalCalories: 720,
    totalProtein: 52,
    totalCarbs: 34,
    totalFat: 42,
    ingredients: [
      {
        id: 'ribeye',
        nameEn: 'Dry-Aged Ribeye Steak (Medium)',
        nameUa: 'Стейк Рібай сухий визрів (Medium)',
        weight_g: 220,
        calories: 510,
        protein_g: 46,
        carbs_g: 0,
        fat_g: 36,
        confidence: 99.1,
        density: 1.08,
        methodEn: 'High-heat seared beef. Complete protein, natural creatine, iron and B12.',
        methodUa: 'Обсмажена яловичина. Повноцінний білок, природний креатин, залізо та B12.',
        category: 'meat',
        topPercent: 54,
        leftPercent: 38
      },
      {
        id: 'sweet_potato',
        nameEn: 'Roasted Sweet Potato Cubes',
        nameUa: 'Запечений батат кубиками',
        weight_g: 140,
        calories: 140,
        protein_g: 2.2,
        carbs_g: 32,
        fat_g: 0.2,
        confidence: 95.8,
        density: 0.88,
        methodEn: 'Oven-roasted with sea salt. High beta-carotene & sustained glycogen replenishment.',
        methodUa: 'Запечений у печі з морською сіллю. Багатий на бета-каротин для відновлення глікогену.',
        category: 'vegetable',
        topPercent: 62,
        leftPercent: 64
      },
      {
        id: 'broccoli',
        nameEn: 'Steamed Tender Broccoli Florets',
        nameUa: 'Броколі на пару з оливковою олією',
        weight_g: 110,
        calories: 42,
        protein_g: 3.5,
        carbs_g: 4.8,
        fat_g: 1.2,
        confidence: 97.4,
        density: 0.48,
        methodEn: 'Steamed al dente. High sulforaphane, vitamin C and cellular detox support.',
        methodUa: 'Приготована аль денте. Високий сульфорафан, вітамін С та клітинний детокс.',
        category: 'vegetable',
        topPercent: 35,
        leftPercent: 62
      },
      {
        id: 'herb_butter',
        nameEn: 'Grass-Fed Rosemary Butter',
        nameUa: 'Трав’яне вершкове масло',
        weight_g: 12,
        calories: 86,
        protein_g: 0.1,
        carbs_g: 0.1,
        fat_g: 9.8,
        confidence: 93.2,
        density: 0.94,
        methodEn: 'Melted grass-fed herb butter. Enhanced fat-soluble nutrient absorption.',
        methodUa: 'Розтоплене масло трав’яного відгодовування. Покращує засвоєння вітамінів.',
        category: 'fat',
        topPercent: 44,
        leftPercent: 46
      }
    ]
  },
  {
    id: 'smash_burger',
    titleEn: 'Double Smash Burger & Fries',
    titleUa: 'Подвійний смеш-бургер з фрі',
    emoji: '🍔',
    taglineEn: 'Decomposing cheat meals into exact macros without the guilt trip',
    taglineUa: 'Точний розбір чіт-мілів без почуття провини та здогадок',
    totalCalories: 890,
    totalProtein: 48,
    totalCarbs: 68,
    totalFat: 46,
    ingredients: [
      {
        id: 'beef_patties',
        nameEn: 'Two Crispy Beef Patties (80/20)',
        nameUa: 'Дві котлети з яловичини (80/20)',
        weight_g: 170,
        calories: 420,
        protein_g: 38,
        carbs_g: 0,
        fat_g: 29,
        confidence: 98.7,
        density: 1.06,
        methodEn: 'Smash-grilled beef patties with crispy Maillard crust.',
        methodUa: 'Обсмажені смеш-котлети з хрусткою скоринкою Майяра.',
        category: 'meat',
        topPercent: 52,
        leftPercent: 40
      },
      {
        id: 'brioche_bun',
        nameEn: 'Toasted Brioche Bun',
        nameUa: 'Підсмажена булочка бріош',
        weight_g: 75,
        calories: 215,
        protein_g: 6.2,
        carbs_g: 38,
        fat_g: 4.8,
        confidence: 96.5,
        density: 0.35,
        methodEn: 'Lightly toasted butter brioche.',
        methodUa: 'Легко підсмажена масляна бріош.',
        category: 'grain',
        topPercent: 36,
        leftPercent: 38
      },
      {
        id: 'cheddar',
        nameEn: 'Melted Aged Cheddar',
        nameUa: 'Розтоплений сир Чеддер',
        weight_g: 30,
        calories: 120,
        protein_g: 7.2,
        carbs_g: 0.4,
        fat_g: 9.8,
        confidence: 95.1,
        density: 1.08,
        methodEn: 'Aged cheddar slice melted over beef.',
        methodUa: 'Шматочок витриманого сиру Чеддер.',
        category: 'dairy',
        topPercent: 46,
        leftPercent: 48
      },
      {
        id: 'fries',
        nameEn: 'Hand-Cut Skin-On Fries',
        nameUa: 'Картопля фрі по-селянськи',
        weight_g: 100,
        calories: 220,
        protein_g: 2.8,
        carbs_g: 30,
        fat_g: 10.5,
        confidence: 97.3,
        density: 0.65,
        methodEn: 'Golden hand-cut fries with sea salt.',
        methodUa: 'Золотиста картопля з морською сіллю.',
        category: 'vegetable',
        topPercent: 62,
        leftPercent: 72
      }
    ]
  },
  {
    id: 'protein_pancakes',
    titleEn: 'Berry Protein Oats & Peanut Butter',
    titleUa: 'Вівсяномлинці з ягодами та арахісом',
    emoji: '🥞',
    taglineEn: 'Fast morning breakfast with sustained energy release & antioxidant boost',
    taglineUa: 'Швидкий сніданок з тривалою енергією та зарядом антиоксидантів',
    totalCalories: 510,
    totalProtein: 38,
    totalCarbs: 56,
    totalFat: 14,
    ingredients: [
      {
        id: 'pancakes',
        nameEn: 'Oat & Whey Protein Flapjacks',
        nameUa: 'Вівсяно-протеїнові панкейки',
        weight_g: 160,
        calories: 280,
        protein_g: 28,
        carbs_g: 34,
        fat_g: 4.5,
        confidence: 97.9,
        density: 0.62,
        methodEn: 'Cooked on dry non-stick skillet. Whey isolate & rolled oats.',
        methodUa: 'Приготовані на сухій пательні. Ізолят сироваткового протеїну та вівсянка.',
        category: 'grain',
        topPercent: 50,
        leftPercent: 38
      },
      {
        id: 'berries',
        nameEn: 'Fresh Blueberries & Raspberries',
        nameUa: 'Свіжа чорниця та малина',
        weight_g: 90,
        calories: 48,
        protein_g: 1.1,
        carbs_g: 10.5,
        fat_g: 0.4,
        confidence: 98.2,
        density: 0.68,
        methodEn: 'Raw wild berries. Rich in anthocyanins & polyphenol antioxidants.',
        methodUa: 'Свіжі лісові ягоди. Багаті на антоціани та поліфеноли.',
        category: 'fruit',
        topPercent: 38,
        leftPercent: 58
      },
      {
        id: 'peanut_butter',
        nameEn: '100% Roasted Peanut Butter Drizzle',
        nameUa: '100% арахісова паста (без цукру)',
        weight_g: 25,
        calories: 148,
        protein_g: 6.8,
        carbs_g: 4.2,
        fat_g: 12.5,
        confidence: 96.0,
        density: 1.15,
        methodEn: 'Pure single-ingredient roasted peanut butter.',
        methodUa: 'Натуральна арахісова паста з цільного горіха.',
        category: 'fat',
        topPercent: 44,
        leftPercent: 42
      },
      {
        id: 'greek_yogurt',
        nameEn: '0% Icelandic Skyr / Greek Yogurt',
        nameUa: 'Грецький йогурт 0% / Скир',
        weight_g: 70,
        calories: 44,
        protein_g: 7.5,
        carbs_g: 2.8,
        fat_g: 0.2,
        confidence: 94.9,
        density: 1.05,
        methodEn: 'Thick strained probiotic yogurt. Extra casein & whey proteins.',
        methodUa: 'Густий пробіотичний йогурт. Додатковий казеїн та сироватковий білок.',
        category: 'dairy',
        topPercent: 60,
        leftPercent: 62
      }
    ]
  }
];

// Density reference foods for Interactive Density Lab
interface DensityFood {
  id: string;
  nameEn: string;
  nameUa: string;
  emoji: string;
  density: number; // g/cm^3
  caloriesPer100g: number;
  proteinPer100g: number;
  carbsPer100g: number;
  fatPer100g: number;
  funFactEn: string;
  funFactUa: string;
}

const DENSITY_FOODS: DensityFood[] = [
  {
    id: 'chicken',
    nameEn: 'Grilled Chicken Breast',
    nameUa: 'Куряче філе гриль',
    emoji: '🍗',
    density: 1.04,
    caloriesPer100g: 165,
    proteinPer100g: 31,
    carbsPer100g: 0,
    fatPer100g: 3.6,
    funFactEn: 'Dense lean muscle tissue with very little air pocket spacing (~1.04 g/cm³).',
    funFactUa: 'Щільна білкова тканина майже без повітряних кишень (~1.04 г/см³).'
  },
  {
    id: 'rice',
    nameEn: 'Fluffy Cooked Jasmine Rice',
    nameUa: 'Варений рис жасмин',
    emoji: '🍚',
    density: 0.72,
    caloriesPer100g: 130,
    proteinPer100g: 2.7,
    carbsPer100g: 28,
    fatPer100g: 0.3,
    funFactEn: 'High void fraction between grains makes cooked rice significantly lighter than solid meat.',
    funFactUa: 'Повітря між зернами робить варений рис значно легшим за щільне м’ясо.'
  },
  {
    id: 'peanut_butter',
    nameEn: 'Natural Peanut Butter',
    nameUa: 'Арахісова паста',
    emoji: '🥜',
    density: 1.15,
    caloriesPer100g: 588,
    proteinPer100g: 25,
    carbsPer100g: 20,
    fatPer100g: 50,
    funFactEn: 'Extremely dense lipid paste (1.15 g/cm³). A single tablespoon weighs more than a handful of popcorn!',
    funFactUa: 'Надзвичайно щільна горіхова паста (1.15 г/см³). 1 ложка важить більше за жменю попкорну!'
  },
  {
    id: 'broccoli',
    nameEn: 'Steamed Broccoli Florets',
    nameUa: 'Броколі на пару',
    emoji: '🥦',
    density: 0.48,
    caloriesPer100g: 35,
    proteinPer100g: 2.8,
    carbsPer100g: 7.2,
    fatPer100g: 0.4,
    funFactEn: 'Over 50% internal air space between florets. Fills your plate with immense volume and minimal calories.',
    funFactUa: 'Понад 50% об’єму — це повітря між суцвіттями. Величезний об’єм на тарілці при мінімумі калорій.'
  },
  {
    id: 'avocado',
    nameEn: 'Fresh Hass Avocado',
    nameUa: 'Свіжий авокадо Хасс',
    emoji: '🥑',
    density: 0.96,
    caloriesPer100g: 160,
    proteinPer100g: 2.0,
    carbsPer100g: 8.5,
    fatPer100g: 14.7,
    funFactEn: 'High water and oleic acid content brings its density right near pure water (0.96 g/cm³).',
    funFactUa: 'Високий вміст води та олеїнової кислоти наближає щільність до чистої води (0.96 г/см³).'
  }
];

export const HowItWorksPage: React.FC<HowItWorksPageProps> = ({
  lang = 'en-US',
  theme = 'light',
  skin = 'default',
  onStartScan,
  onBack
}) => {
  const isUa = lang.startsWith('ua');

  // --- STATE DECLARATIONS (Hoisted at top to prevent TDZ) ---
  const [selectedDishIndex, setSelectedDishIndex] = useState(0);
  const [activeIngredientId, setActiveIngredientId] = useState<string | null>(
    SAMPLE_DISHES[0].ingredients[0].id
  );
  const [selectedDensityFoodId, setSelectedDensityFoodId] = useState<string>(DENSITY_FOODS[0].id);
  const [interactiveVolumeCm3, setInteractiveVolumeCm3] = useState<number>(180);
  const [isLightingNormalized, setIsLightingNormalized] = useState<boolean>(true);
  const [expandedFaqIndex, setExpandedFaqIndex] = useState<number | null>(null);

  // --- MEMOIZED HELPERS ---
  const currentDish = SAMPLE_DISHES[selectedDishIndex] || SAMPLE_DISHES[0];
  const activeIngredient = useMemo(() => {
    return currentDish.ingredients.find(i => i.id === activeIngredientId) || currentDish.ingredients[0];
  }, [currentDish, activeIngredientId]);

  const currentDensityFood = useMemo(() => {
    return DENSITY_FOODS.find(f => f.id === selectedDensityFoodId) || DENSITY_FOODS[0];
  }, [selectedDensityFoodId]);

  // Calculated values in the 3D Density Lab
  const calculatedWeightG = Math.round(interactiveVolumeCm3 * currentDensityFood.density);
  const calculatedRatio = calculatedWeightG / 100;
  const calculatedCalories = Math.round(currentDensityFood.caloriesPer100g * calculatedRatio);
  const calculatedProtein = Math.round(currentDensityFood.proteinPer100g * calculatedRatio * 10) / 10;
  const calculatedCarbs = Math.round(currentDensityFood.carbsPer100g * calculatedRatio * 10) / 10;
  const calculatedFat = Math.round(currentDensityFood.fatPer100g * calculatedRatio * 10) / 10;

  // Skin theme styles
  const isStalker = skin === 'stalker';
  const isCyberpunk = skin === 'cyberpunk';
  const isAnime = skin === 'anime';

  const accentBgClass = isStalker
    ? "bg-[#4ade80] text-black shadow-lg shadow-[#4ade80]/20"
    : isCyberpunk
      ? "bg-[#FCEE0A] text-black shadow-lg shadow-[#FCEE0A]/20"
      : isAnime
        ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/20"
        : "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25 dark:shadow-emerald-950/50 hover:bg-emerald-400 dark:hover:bg-emerald-400";

  const cardBorderClass = isStalker
    ? "border-[#4ade80]/30 bg-black"
    : isCyberpunk
      ? "border-[#00ffcc]/30 bg-[#0d0f18]"
      : isAnime
        ? "border-pink-500/20 bg-white/90 dark:bg-zinc-900/90"
        : "border-[var(--card-border)] bg-[var(--card-bg)]";

  // FAQ items
  const faqs = useMemo(() => [
    {
      qEn: "How accurate is the AI compared to physical kitchen scales?",
      qUa: "Наскільки AI точний у порівнянні зі звичайними кухонними вагами?",
      aEn: "Our 3D volumetric model achieves ~92-96% accuracy on typical whole food plates. For everyday fitness, bodybuilding, and weight management, that 4-8% variance is practically negligible and saves you 15+ minutes of tedious weighing every single day. If something feels off, you can adjust the weight with one tap.",
      aUa: "Наша 3D об'ємна модель дає ~92-96% точності на звичайних порціях. Для фітнесу, схуднення та набору форми похибка у 4-8% не грає ролі, зате економить купу часу без зважування кожної крихти. Якщо треба — вагу можна підправити в один клік."
    },
    {
      qEn: "What about complex stews, curries, and multi-ingredient soups?",
      qUa: "А що робити зі складними супами, рагу чи запіканками?",
      aEn: "The AI recognizes the composite base broth/sauce and detects visible surface chunks (beef, potatoes, carrots, cream). If ingredients are hidden inside (like extra butter or sugar), you can mention them in the instant voice/text prompt (e.g., 'with 2 tbsp olive oil') and the neural engine recalculates the macros immediately.",
      aUa: "Нейромережа розпізнає основу соусу та видимі шматки продуктів. Якщо всередині є приховані інгредієнти (наприклад, 2 ложки масла), просто надиктуй або допиши це в нотатках — AI миттєво оновить макроси."
    },
    {
      qEn: "How does it detect hidden oils, butter, and frying methods?",
      qUa: "Як додаток розуміє, смажена страва на олії чи зварена?",
      aEn: "Our vision model analyzes surface specular highlights (sheen/gloss), char markers (Maillard reaction crust), and color depth. High sheen on chicken breast indicates pan-frying in fat, while a matte pale texture signifies poaching or steaming.",
      aUa: "Зір аналізує відблиски світла на поверхні (жирний блиск), скоринку реакції Майяра та текстуру. Глянцевий блиск сигналізує про смаження на олії, а матова текстура — про варіння або запікання."
    },
    {
      qEn: "Does it work with restaurant dishes and takeaway meals?",
      qUa: "Чи працює це з їжею в ресторанах та доставках?",
      aEn: "Yes! The AI is trained on extensive global culinary datasets — from local sushi rolls, Neapolitan pizzas, and Kyiv cutlets to fast-food favorites. It accurately predicts restaurant-style higher butter/sodium baselines.",
      aUa: "Так! AI навчений на світових базах рецептів — від суші та піци до котлет по-київськи та страв з закладів. Він враховує ресторанну специфіку приготування з більшою кількістю масла та соусів."
    }
  ], []);

  return (
    <div className="min-h-screen pb-32 pt-2 px-3 sm:px-6 max-w-4xl mx-auto space-y-8 animate-fadeIn">
      {/* Top Breadcrumb & Status Bar */}
      <div className="flex items-center justify-between gap-2 pt-2">
        <div className="flex items-center gap-2">
          {onBack && (
            <button
              onClick={() => {
                sounds.playPop();
                triggerHaptic('SELECTION');
                onBack();
              }}
              className="p-2 rounded-xl bg-zinc-100 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 border border-zinc-200/80 dark:border-zinc-800 transition-all cursor-pointer flex items-center justify-center shadow-xs"
              title={isUa ? "Назад" : "Back"}
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
          )}
        </div>

        <button
          onClick={() => {
            sounds.playPop();
            triggerHaptic('SUCCESS');
            onStartScan();
          }}
          className={cn(
            "px-3.5 py-1.5 rounded-xl text-[11px] sm:text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md hover:scale-105 active:scale-95 cursor-pointer ml-auto",
            accentBgClass
          )}
        >
          <Camera className="w-3.5 h-3.5" />
          <span>{isUa ? "Спробувати скан" : "Try Scan"}</span>
        </button>
      </div>

      {/* Hero Banner Section */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-2xl relative overflow-hidden",
        cardBorderClass,
        "shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]"
      )}>
        {/* Enhanced Futuristic Background Effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {/* Tech Grid Mesh with radial falloff */}
          <div className="absolute inset-0 bg-tech-grid opacity-35 dark:opacity-20" />
          
          {/* Multi-layered Animated Floating Glowing Orbs */}
          <motion.div 
            animate={{ y: [0, -18, 0], x: [0, 14, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className={cn(
              "absolute -top-20 -right-12 w-72 h-72 rounded-full blur-3xl",
              isStalker ? "bg-[#4ade80]/20" : isCyberpunk ? "bg-[#00ffcc]/20" : isAnime ? "bg-pink-500/20" : "bg-emerald-500/20 dark:bg-emerald-500/30"
            )}
          />
          <motion.div 
            animate={{ y: [0, 22, 0], x: [0, -18, 0], scale: [1, 1.18, 1] }}
            transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className={cn(
              "absolute -bottom-20 -left-12 w-80 h-80 rounded-full blur-3xl",
              isStalker ? "bg-[#4ade80]/15" : isCyberpunk ? "bg-[#FCEE0A]/15" : isAnime ? "bg-purple-600/20" : "bg-cyan-500/20 dark:bg-cyan-500/30"
            )}
          />
          <motion.div 
            animate={{ scale: [0.9, 1.15, 0.9], opacity: [0.15, 0.35, 0.15] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className={cn(
              "absolute top-1/3 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full blur-3xl",
              isStalker ? "bg-emerald-400/20" : isCyberpunk ? "bg-cyan-400/15" : isAnime ? "bg-fuchsia-400/20" : "bg-teal-400/15 dark:bg-teal-400/25"
            )}
          />

          {/* Corner Reticle Brackets for Futuristic HUD aesthetic */}
          <span className="absolute top-3 left-3 w-3 h-3 border-l-2 border-t-2 border-emerald-500/40 dark:border-emerald-400/60 pointer-events-none" />
          <span className="absolute top-3 right-3 w-3 h-3 border-r-2 border-t-2 border-emerald-500/40 dark:border-emerald-400/60 pointer-events-none" />
          <span className="absolute bottom-3 left-3 w-3 h-3 border-l-2 border-b-2 border-emerald-500/40 dark:border-emerald-400/60 pointer-events-none" />
          <span className="absolute bottom-3 right-3 w-3 h-3 border-r-2 border-b-2 border-emerald-500/40 dark:border-emerald-400/60 pointer-events-none" />
        </div>

        <div className="relative z-10 space-y-4 text-center">
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-[var(--text)] leading-tight text-center">
            {isStalker ? (
              isUa ? "БІО-СКАНЕР ЗОНИ: ЯК PDA РОЗПІЗНАЄ ЇЖУ" : "ZONE BIO-SCANNER: HOW PDA ANALYZES RATIONS"
            ) : isCyberpunk ? (
              isUa ? "КІБЕР-ЗІР: МИТТЄВА ДЕКОМПОЗИЦІЯ СТРАВИ" : "CYBER-VISION: INSTANT MEAL DECONSTRUCTION"
            ) : (
              isUa ? (
                <>Як штучний інтелект <br className="hidden md:block" /><span className="text-emerald-500 dark:text-emerald-400">сканує та рахує</span> вашу їжу</>
              ) : (
                <>How the AI Meal Scanner <br className="hidden md:block" /><span className="text-emerald-500 dark:text-emerald-400">Actually Works</span></>
              )
            )}
          </h1>

          <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-300 leading-relaxed max-w-2xl font-normal text-center mx-auto">
            {isUa ? (
              "Ніяких кухонних ваг, нудних щоденників і ручного вбивання калорій у таблички. Ти просто робиш одне фото тарілки — а нейромережа за 1.8 секунди сегментує кожен інгредієнт, прораховує його 3D-об'єм, звіряє з лабораторними базами і видає точні макроси."
            ) : (
              "No kitchen scales, no tedious spreadsheets, no endless manual logging. You take one photo of your plate — our multi-modal neural network isolates every single ingredient, approximates its 3D volume, cross-references verified lab databases, and serves up exact macros in under 2 seconds."
            )}
          </p>

          {/* Quick Metrics Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-3">
            <div className="p-3 rounded-2xl bg-zinc-100/90 dark:bg-zinc-800/70 border border-zinc-200/70 dark:border-zinc-700/70 flex flex-col justify-center shadow-xs">
              <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-xs font-black">
                <Zap className="w-3.5 h-3.5 fill-emerald-500 dark:fill-emerald-400" />
                <span>&lt; 1.8s</span>
              </div>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                {isUa ? "Швидкість скану" : "Scan Latency"}
              </span>
            </div>

            <div className="p-3 rounded-2xl bg-zinc-100/90 dark:bg-zinc-800/70 border border-zinc-200/70 dark:border-zinc-700/70 flex flex-col justify-center shadow-xs">
              <div className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400 text-xs font-black">
                <Scale className="w-3.5 h-3.5" />
                <span>94.6%</span>
              </div>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                {isUa ? "Точність ваги" : "Portion Precision"}
              </span>
            </div>

            <div className="p-3 rounded-2xl bg-zinc-100/90 dark:bg-zinc-800/70 border border-zinc-200/70 dark:border-zinc-700/70 flex flex-col justify-center shadow-xs">
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs font-black">
                <Utensils className="w-3.5 h-3.5" />
                <span>10,000+</span>
              </div>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                {isUa ? "База страв" : "Dishes Trained"}
              </span>
            </div>

            <div className="p-3 rounded-2xl bg-zinc-100/90 dark:bg-zinc-800/70 border border-zinc-200/70 dark:border-zinc-700/70 flex flex-col justify-center shadow-xs">
              <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400 text-xs font-black">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Grade A</span>
              </div>
              <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                {isUa ? "E-Shield & Безпека" : "E-Additive Shield"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Image Banner: Futuristic HUD Scan */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-emerald-500/20 dark:from-emerald-500/30 dark:via-cyan-500/30 dark:to-emerald-500/30 rounded-[34px] blur-xl opacity-60 dark:opacity-75 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
        <div className={cn(
          "rounded-3xl border overflow-hidden relative shadow-2xl",
          cardBorderClass
        )}>
          <div className="relative aspect-[16/9] w-full bg-zinc-950">
            <img 
              src="/how_it_works_hud.jpg" 
              alt="AI Meal Vision Scanning HUD"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105 opacity-90"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-4 sm:p-6 text-white">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-emerald-500 dark:bg-emerald-400 text-black text-[10px] font-black uppercase tracking-widest flex items-center gap-1 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-black animate-ping" />
                  {isUa ? "МУЛЬТИМОДАЛЬНИЙ ЗІР У ДІЇ" : "NEURAL VISION IN ACTION"}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-zinc-200 font-medium mt-1.5 max-w-xl">
                {isUa 
                  ? "ШІ автоматично розпізнає та виділяє контури лосося, авокадо, кіноа та спаржі, визначаючи тип термообробки."
                  : "Neural vision highlights individual food bounding segments and computes macro density for each component."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- INTERACTIVE ELEMENT 1: Live AI Plate Inspector --- */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-xl space-y-6 relative overflow-hidden",
        cardBorderClass
      )}>
        {/* Background Ambient Depth */}
        <div className="absolute inset-0 bg-tech-grid opacity-25 dark:opacity-15 pointer-events-none" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-16 w-80 h-80 bg-cyan-500/10 dark:bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="text-center max-w-xl mx-auto space-y-1 relative z-10">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Спробуй AI сканер прямо зараз" : "Live AI Plate Inspector"}
          </h2>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
            {isUa 
              ? "Обери страву нижче та натисни на будь-який інгредієнт, щоб побачити роботу нейромережі:"
              : "Select a sample dish below and tap any ingredient to inspect its neural vision breakdown:"}
          </p>
        </div>

        {/* Dish Switcher Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 relative z-10">
          {SAMPLE_DISHES.map((dish, idx) => {
            const isSelected = selectedDishIndex === idx;
            return (
              <button
                key={dish.id}
                onClick={() => {
                  sounds.playPop();
                  triggerHaptic('SELECTION');
                  setSelectedDishIndex(idx);
                  setActiveIngredientId(dish.ingredients[0].id);
                }}
                className={cn(
                  "p-3 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1 shadow-xs",
                  isSelected
                    ? (isStalker 
                        ? "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80]" 
                        : isCyberpunk 
                          ? "bg-[#FCEE0A]/20 border-[#FCEE0A] text-[#FCEE0A]" 
                          : "bg-emerald-500/15 dark:bg-emerald-500/20 border-emerald-500 dark:border-emerald-400 text-emerald-600 dark:text-emerald-400 font-bold shadow-md shadow-emerald-500/10 dark:shadow-emerald-950/40")
                    : "bg-zinc-100/70 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700/80 text-zinc-700 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/90"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl">{dish.emoji}</span>
                  {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />}
                </div>
                <span className="text-xs font-bold truncate text-[var(--text)]">
                  {isUa ? dish.titleUa : dish.titleEn}
                </span>
                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-mono">
                  {dish.totalCalories} kcal • {dish.totalProtein}g P
                </span>
              </button>
            );
          })}
        </div>

        {/* Interactive Plate Container */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start relative z-10">
          {/* Left Column: Interactive Ingredient Overlay List */}
          <div className="lg:col-span-6 space-y-2.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
              {isUa ? "Розпізнані компоненти страви:" : "Detected Meal Components:"}
            </span>

            <div className="space-y-2">
              {currentDish.ingredients.map((ing) => {
                const isActive = activeIngredient.id === ing.id;
                return (
                  <button
                    key={ing.id}
                    onClick={() => {
                      sounds.playPop();
                      triggerHaptic('SELECTION');
                      setActiveIngredientId(ing.id);
                    }}
                    className={cn(
                      "w-full p-3.5 rounded-2xl border text-left transition-all cursor-pointer flex items-center justify-between gap-3 shadow-xs",
                      isActive
                        ? "bg-emerald-500/15 dark:bg-emerald-500/20 border-emerald-500 dark:border-emerald-400 shadow-md shadow-emerald-500/10 dark:shadow-emerald-950/40"
                        : "bg-zinc-100/60 dark:bg-zinc-800/50 border-zinc-200/80 dark:border-zinc-700/70 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/80"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-7 h-7 rounded-xl flex items-center justify-center font-black text-xs shrink-0 transition-transform",
                        isActive ? "bg-emerald-500 text-white scale-110 shadow-sm" : "bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200"
                      )}>
                        {ing.category === 'meat' || ing.category === 'fish' ? <Beef className="w-3.5 h-3.5" /> : ing.category === 'grain' ? <Wheat className="w-3.5 h-3.5" /> : ing.category === 'fat' ? <Droplets className="w-3.5 h-3.5" /> : <Carrot className="w-3.5 h-3.5" />}
                      </div>
                      <div>
                        <h4 className="text-xs sm:text-sm font-bold text-[var(--text)]">
                          {isUa ? ing.nameUa : ing.nameEn}
                        </h4>
                        <div className="flex items-center gap-2 text-[10px] text-zinc-500 dark:text-zinc-400 font-mono mt-0.5">
                          <span>{ing.weight_g}g</span>
                          <span>•</span>
                          <span>{ing.calories} kcal</span>
                          <span>•</span>
                          <span className="text-emerald-600 dark:text-emerald-400 font-bold">{ing.confidence}% match</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-zinc-200/80 dark:bg-zinc-800 border border-zinc-300/40 dark:border-zinc-700/80 text-zinc-700 dark:text-zinc-300 font-mono">
                        {ing.protein_g}g P
                      </span>
                      <ChevronRight className={cn("w-4 h-4 text-zinc-400 dark:text-zinc-500 transition-transform", isActive && "rotate-90 text-emerald-500 dark:text-emerald-400")} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Column: AI Deep Breakdown Card for Selected Item */}
          <div className="lg:col-span-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIngredient.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
                className={cn(
                  "p-5 sm:p-6 rounded-2xl border space-y-4 shadow-lg relative overflow-hidden",
                  isStalker 
                    ? "bg-black border-[#4ade80]/40" 
                    : isCyberpunk 
                      ? "bg-[#0d0f18] border-[#00ffcc]/30"
                      : isAnime
                        ? "bg-white/90 dark:bg-zinc-900/90 border-pink-500/30"
                        : "bg-white dark:bg-zinc-900/90 border-zinc-200 dark:border-zinc-800"
                )}
              >
                {/* Header with Title & Confidence badge */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                      {isUa ? "ДЕКОМПОЗИЦІЯ ІНГРЕДІЄНТА" : "INGREDIENT TELEMETRY"}
                    </span>
                    <h3 className="text-base sm:text-lg font-black text-[var(--text)]">
                      {isUa ? activeIngredient.nameUa : activeIngredient.nameEn}
                    </h3>
                  </div>

                  <div className="px-2.5 py-1 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-xs font-mono font-bold shrink-0">
                    {activeIngredient.confidence}% {isUa ? "довіра" : "confidence"}
                  </div>
                </div>

                {/* Macro Pills Grid */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-2.5 rounded-xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200/60 dark:border-zinc-700/70 text-center shadow-xs">
                    <span className="text-[10px] uppercase font-bold text-zinc-500 dark:text-zinc-400 block">{isUa ? "Калорії" : "Calories"}</span>
                    <span className="text-sm font-black text-[var(--text)] font-mono">{activeIngredient.calories}</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/40 text-center shadow-xs">
                    <span className="text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-400 block">{isUa ? "Білки" : "Protein"}</span>
                    <span className="text-sm font-black text-emerald-600 dark:text-emerald-400 font-mono">{activeIngredient.protein_g}g</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/20 dark:border-amber-500/40 text-center shadow-xs">
                    <span className="text-[10px] uppercase font-bold text-amber-600 dark:text-amber-400 block">{isUa ? "Вуглеводи" : "Carbs"}</span>
                    <span className="text-sm font-black text-amber-600 dark:text-amber-400 font-mono">{activeIngredient.carbs_g}g</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-red-500/10 dark:bg-red-500/20 border border-red-500/20 dark:border-red-500/40 text-center shadow-xs">
                    <span className="text-[10px] uppercase font-bold text-red-600 dark:text-red-400 block">{isUa ? "Жири" : "Fats"}</span>
                    <span className="text-sm font-black text-red-600 dark:text-red-400 font-mono">{activeIngredient.fat_g}g</span>
                  </div>
                </div>

                {/* AI Reasoning Insight Box */}
                <div className="p-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-1.5 shadow-xs">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--text)]">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                    <span>{isUa ? "Чому AI визначив це саме так:" : "Neural Vision Reasoning:"}</span>
                  </div>
                  <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                    {isUa ? activeIngredient.methodUa : activeIngredient.methodEn}
                  </p>
                </div>

                {/* 3D Geometry Stats */}
                <div className="flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400 border-t border-zinc-200 dark:border-zinc-800 pt-3">
                  <span>{isUa ? "Розрахована вага:" : "Volumetric Weight:"} <strong className="text-[var(--text)]">{activeIngredient.weight_g}g</strong></span>
                  <span>{isUa ? "Коефіцієнт щільності:" : "Density Coeff:"} <strong className="text-[var(--text)]">{activeIngredient.density} g/cm³</strong></span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Visual Image Banner 2: Volumetric Point Cloud Mesh */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/25 via-blue-500/20 to-teal-500/25 dark:from-cyan-500/35 dark:via-blue-500/30 dark:to-teal-500/35 rounded-[34px] blur-xl opacity-50 dark:opacity-70 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
        <div className={cn(
          "rounded-3xl border overflow-hidden relative shadow-2xl",
          cardBorderClass
        )}>
          <div className="relative aspect-[16/9] w-full bg-zinc-950">
            <img 
              src="/how_it_works_volumetric.jpg" 
              alt="3D Volumetric Food Depth & Density Scanning"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105 opacity-90"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-4 sm:p-6 text-white">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-cyan-500 dark:bg-cyan-400 text-black text-[10px] font-black uppercase tracking-widest flex items-center gap-1 shadow-sm">
                  <Scale className="w-3 h-3" />
                  {isUa ? "3D ПРОСТОРОВА ТОПОГРАФІЯ" : "3D TOPOGRAPHIC DENSITY MAPPING"}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-zinc-200 font-medium mt-1.5 max-w-xl">
                {isUa 
                  ? "Просторова сітка зчитує висоту (вісь Z) та контури страви, перетворюючи пікселі на точні грами через коефіцієнт щільності."
                  : "Height contours and spatial depth convert 2D camera pixels into precise physical gram weights using material density."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- CORE 6-STEP NEURAL PIPELINE (The Bro-Tone Breakdown) --- */}
      <div className="space-y-4 relative">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="text-center max-w-xl mx-auto space-y-1 relative z-10">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Покроковий пайплайн: Від фото до макросів" : "The 6-Step Neural Scanning Pipeline"}
          </h2>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
            {isUa 
              ? "Ось що відбувається за ті 1.8 секунди, поки ти бачиш анімацію сканування:" 
              : "Here is what executes behind the scenes during those 1.8 seconds of scanning:"}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 relative z-10">
          {/* Step 1 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-emerald-500/50 dark:hover:border-emerald-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-emerald-500/15 dark:bg-emerald-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-black text-sm shadow-xs">
                01
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 1 • Оптика" : "Step 1 • Optics"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "1. Оптична нормалізація та фільтрація відблисків" : "1. Optical Normalization & Glare Removal"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Як тільки ти робиш фото, алгоритм виправляє кут нахилу смартфона, прибирає затінення від рук та нейтралізує спалах, готуючи чисту матрицю пікселів для нейромережі."
              ) : (
                "The instant you capture a photo, localized edge-enhancement normalizes ambient shadows, corrects gyro angle, and eliminates flash glare before tokenizing the image."
              )}
            </p>
          </div>

          {/* Step 2 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-cyan-500/50 dark:hover:border-cyan-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-cyan-500/15 dark:bg-cyan-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-cyan-500/10 dark:bg-cyan-500/20 border border-cyan-500/20 dark:border-cyan-500/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-black text-sm shadow-xs">
                02
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 2 • Зір" : "Step 2 • Vision"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "2. Мультимодальний зір Gemini Vision" : "2. Multi-Modal Deep Neural Vision"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Модель не просто бачить 'щось коричневе'. Вона розпізнає мікротекстуру: скоринку обсмаження (реакція Майяра), глянець олії, ступінь запікання і тип нарізки."
              ) : (
                "Gemini Vision examines surface micro-textures, sear marks, glossiness, grain structure, and browning depth to distinguish fried cutlets from poached chicken."
              )}
            </p>
          </div>

          {/* Step 3 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-amber-500/50 dark:hover:border-amber-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-amber-500/15 dark:bg-amber-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/20 dark:border-amber-500/40 text-amber-600 dark:text-amber-400 flex items-center justify-center font-black text-sm shadow-xs">
                03
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 3 • Сегментація" : "Step 3 • Segmentation"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "3. Декомпозиція складної страви на деталі" : "3. Semantic Ingredient Decomposition"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Складна страва не рахується 'на око'. Бургер розбивається на булочку бріош, дві яловичі котлети, сир чеддер та соус, вираховуючи окремий внесок кожного."
              ) : (
                "Complex meals aren't a mystery box. A double burger is split into individual brioche buns, 80/20 patties, melted cheddar, and secret sauce dressing."
              )}
            </p>
          </div>

          {/* Step 4 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-purple-500/50 dark:hover:border-purple-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-purple-500/15 dark:bg-purple-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-purple-500/10 dark:bg-purple-500/20 border border-purple-500/20 dark:border-purple-500/40 text-purple-600 dark:text-purple-400 flex items-center justify-center font-black text-sm shadow-xs">
                04
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 4 • 3D Об'єм" : "Step 4 • 3D Volume"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "4. 3D просторовий об'єм та щільність (г/см³)" : "4. 3D Volumetric Depth & Density Math"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Використовуючи край тарілки (~26 см) чи прибори як масштаб, AI визначає об'єм у см³ і множить його на специфічну щільність продукту (варений рис 0.72 vs стейк 1.05 г/см³)."
              ) : (
                "Using plate geometry (~26cm rim anchor) and perspective depth, the algorithm calculates physical cm³ volume and applies food-specific density coefficients (g/cm³)."
              )}
            </p>
          </div>

          {/* Step 5 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-emerald-500/50 dark:hover:border-emerald-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-emerald-500/15 dark:bg-emerald-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-black text-sm shadow-xs">
                05
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 5 • Звірка баз" : "Step 5 • Verification"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "5. Крос-звірка з базами USDA та EuroFIR" : "5. Verified Nutritional Lab Mapping"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Отримані грами миттєво звіряються з офіційними біохімічними таблицями харчової цінності: калорії, білки, жири, вуглеводи, клітковина, натрій та глікемічний індекс."
              ) : (
                "Calculated gram weights map directly against USDA FoodData Central and EuroFIR lab tables for complete macronutrients, fiber, sodium, and glycemic load."
              )}
            </p>
          </div>

          {/* Step 6 */}
          <div className={cn("p-5 rounded-3xl border space-y-3 relative card-shadow overflow-hidden group hover:border-rose-500/50 dark:hover:border-rose-400/60 transition-all", cardBorderClass)}>
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-rose-500/15 dark:bg-rose-500/20 rounded-full blur-2xl pointer-events-none group-hover:scale-125 transition-transform" />
            <div className="flex items-center justify-between relative z-10">
              <div className="w-9 h-9 rounded-2xl bg-rose-500/10 dark:bg-rose-500/20 border border-rose-500/20 dark:border-rose-500/40 text-rose-600 dark:text-rose-400 flex items-center justify-center font-black text-sm shadow-xs">
                06
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? "Крок 6 • Біо-Захист" : "Step 6 • Longevity"}
              </span>
            </div>
            <h3 className="text-base font-bold text-[var(--text)] relative z-10">
              {isUa ? "6. Longevity Shield, алергени та E-Decoder" : "6. Longevity Shield & Allergen Guardrails"}
            </h3>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed relative z-10">
              {isUa ? (
                "Перевірка на твої персональні алергени (глютен, лактоза, горіхи), підрахунок кінцевих продуктів глікації (AGEs) та детекція шкідливих ультраоброблених E-добавок."
              ) : (
                "Live checks for personal allergens (gluten, dairy, nuts), dietary inflammatory scoring, AGEs glycation index, and synthetic E-additive hazard detection."
              )}
            </p>
          </div>
        </div>
      </div>

      {/* --- INTERACTIVE ELEMENT 2: Volumetric 3D Density & Portion Lab --- */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-xl space-y-6 relative overflow-hidden",
        cardBorderClass
      )}>
        {/* Background Ambient Depth */}
        <div className="absolute inset-0 bg-tech-grid-cyan opacity-25 dark:opacity-15 pointer-events-none" />
        <motion.div 
          animate={{ y: [0, -15, 0], x: [0, 10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -right-16 -top-16 w-72 h-72 bg-cyan-500/15 dark:bg-cyan-500/25 rounded-full blur-3xl pointer-events-none" 
        />
        <motion.div 
          animate={{ y: [0, 15, 0], x: [0, -10, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -left-16 -bottom-16 w-72 h-72 bg-emerald-500/15 dark:bg-emerald-500/25 rounded-full blur-3xl pointer-events-none" 
        />
        <div className="text-center max-w-xl mx-auto space-y-1 relative z-10">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Чому об'єм ≠ вага: Інтерактивний калькулятор" : "Why Volume ≠ Weight: The Density Simulator"}
          </h2>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
            {isUa 
              ? "Потягни повзунок об'єму та обери продукт, щоб побачити як формула ваги (г) = об'єм (см³) × щільність змінює макроси:"
              : "Drag the 3D volume slider and pick a food to see how the formula Weight (g) = Volume (cm³) × Density computes real macros:"}
          </p>
        </div>

        {/* Food Type Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none relative z-10">
          {DENSITY_FOODS.map((food) => {
            const isSelected = selectedDensityFoodId === food.id;
            return (
              <button
                key={food.id}
                onClick={() => {
                  sounds.playPop();
                  triggerHaptic('SELECTION');
                  setSelectedDensityFoodId(food.id);
                }}
                className={cn(
                  "px-3.5 py-2 rounded-2xl border shrink-0 text-left transition-all cursor-pointer flex items-center gap-2 shadow-xs",
                  isSelected
                    ? "bg-cyan-500/15 dark:bg-cyan-500/20 border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 font-bold shadow-md shadow-cyan-500/10 dark:shadow-cyan-950/40"
                    : "bg-zinc-100/70 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700/80 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200/50 dark:hover:bg-zinc-700/60 hover:border-zinc-300 dark:hover:border-zinc-600"
                )}
              >
                <span className="text-base">{food.emoji}</span>
                <div className="flex flex-col">
                  <span className="text-xs font-bold leading-tight text-[var(--text)]">{isUa ? food.nameUa : food.nameEn}</span>
                  <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-mono">ρ = {food.density} g/cm³</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Interactive Volume Slider */}
        <div className="p-5 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-4 relative z-10 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
              {isUa ? "3D Просторовий об'єм на тарілці:" : "3D Spatial Plate Volume:"}
            </span>
            <span className="text-sm font-black text-cyan-600 dark:text-cyan-400 font-mono">
              {interactiveVolumeCm3} cm³
            </span>
          </div>

          <input 
            type="range" 
            min="50" 
            max="350" 
            step="5"
            value={interactiveVolumeCm3}
            onChange={(e) => {
              setInteractiveVolumeCm3(Number(e.target.value));
              triggerHaptic('SELECTION');
            }}
            className="w-full accent-cyan-500 dark:accent-cyan-400 h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg cursor-pointer"
          />

          <div className="flex justify-between text-[10px] text-zinc-500 dark:text-zinc-400 font-mono">
            <span>50 cm³ ({isUa ? "Маленька порція" : "Small Garnish"})</span>
            <span>200 cm³ ({isUa ? "Середня страва" : "Standard Serving"})</span>
            <span>350 cm³ ({isUa ? "Велика порція" : "Large Portion"})</span>
          </div>
        </div>

        {/* Live Calculation Output Card */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-emerald-500/5 to-transparent dark:from-cyan-950/40 dark:via-zinc-900/80 dark:to-zinc-900/90 border border-cyan-500/30 dark:border-cyan-500/40 space-y-4 relative z-10 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400">
                {isUa ? "РОЗРАХОВАНА МАСА ТА МАКРОСИ" : "CALCULATED MASS & NUTRITION"}
              </span>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-3xl font-black text-[var(--text)] font-mono">
                  {calculatedWeightG}g
                </span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                  ({interactiveVolumeCm3} cm³ × {currentDensityFood.density} g/cm³)
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/15 dark:bg-cyan-500/20 border border-cyan-500/30 dark:border-cyan-500/40 text-cyan-600 dark:text-cyan-400 text-sm font-black font-mono self-start sm:self-center shadow-xs">
              <Flame className="w-4 h-4 fill-cyan-500 dark:fill-cyan-400 text-cyan-500 dark:text-cyan-400" />
              <span>{calculatedCalories} kcal</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="p-3 rounded-xl bg-white/90 dark:bg-zinc-800/80 border border-zinc-200/80 dark:border-zinc-700/70 text-center shadow-xs">
              <span className="text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400 block">{isUa ? "Білок" : "Protein"}</span>
              <span className="text-base font-black text-[var(--text)] font-mono">{calculatedProtein}g</span>
            </div>
            <div className="p-3 rounded-xl bg-white/90 dark:bg-zinc-800/80 border border-zinc-200/80 dark:border-zinc-700/70 text-center shadow-xs">
              <span className="text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400 block">{isUa ? "Вуглеводи" : "Carbs"}</span>
              <span className="text-base font-black text-[var(--text)] font-mono">{calculatedCarbs}g</span>
            </div>
            <div className="p-3 rounded-xl bg-white/90 dark:bg-zinc-800/80 border border-zinc-200/80 dark:border-zinc-700/70 text-center shadow-xs">
              <span className="text-[10px] font-bold uppercase text-red-600 dark:text-red-400 block">{isUa ? "Жири" : "Fats"}</span>
              <span className="text-base font-black text-[var(--text)] font-mono">{calculatedFat}g</span>
            </div>
          </div>

          {/* Density Fun Fact */}
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-zinc-200/50 dark:bg-zinc-800/70 border border-zinc-300/40 dark:border-zinc-700/60 text-xs text-zinc-600 dark:text-zinc-300">
            <Info className="w-4 h-4 text-cyan-500 dark:text-cyan-400 shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              <strong className="text-[var(--text)]">{isUa ? "Біо-факт:" : "Bio Fact:"}</strong>{" "}
              {isUa ? currentDensityFood.funFactUa : currentDensityFood.funFactEn}
            </p>
          </div>
        </div>
      </div>

      {/* --- PRO TIPS FOR 99% SCAN ACCURACY --- */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-xl space-y-5 relative overflow-hidden",
        cardBorderClass
      )}>
        {/* Ambient Glows */}
        <div className="absolute -top-20 -left-16 w-64 h-64 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-16 w-64 h-64 bg-amber-500/10 dark:bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="text-center max-w-xl mx-auto space-y-1 relative z-10">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Як отримати ідеальний скан з першого разу" : "How to get 99% pinpoint scan accuracy"}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 relative z-10">
          <div className="p-4 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-2 hover:border-emerald-500/40 dark:hover:border-emerald-500/50 transition-colors shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-emerald-500/20 dark:bg-emerald-500/25 border border-emerald-500/40 dark:border-emerald-500/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-xs font-black shrink-0">1</span>
              <h4 className="text-xs sm:text-sm font-bold text-[var(--text)]">
                {isUa ? "Кут огляду 45 градусів — ідеал" : "The 45-Degree Sweet Spot"}
              </h4>
            </div>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed pl-8.5">
              {isUa 
                ? "Знімок під кутом ~45° дає нейромережі одночасно площу та висоту порції. Плоске фото строго зверху може недооцінити товщину стейка чи шар пюре."
                : "A 45-degree angle captures both surface area AND 3D height profile. Flat top-down shots can slightly underestimate steak thickness or layered rice."}
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-2 hover:border-emerald-500/40 dark:hover:border-emerald-500/50 transition-colors shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-emerald-500/20 dark:bg-emerald-500/25 border border-emerald-500/40 dark:border-emerald-500/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-xs font-black shrink-0">2</span>
              <h4 className="text-xs sm:text-sm font-bold text-[var(--text)]">
                {isUa ? "Тарілка або виделка у кадрі" : "Include the Plate or Fork as Anchor"}
              </h4>
            </div>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed pl-8.5">
              {isUa 
                ? "Коли у кадрі видно край тарілки чи виделку, алгоритм автоматично калібрує масштаб (діаметр стандартної тарілки 26-28 см) для надточної ваги."
                : "Having the plate rim or a fork in the frame gives the AI an instant spatial reference (standard ~26cm dinner plate) to lock in gram calibration."}
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-2 hover:border-emerald-500/40 dark:hover:border-emerald-500/50 transition-colors shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-emerald-500/20 dark:bg-emerald-500/25 border border-emerald-500/40 dark:border-emerald-500/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-xs font-black shrink-0">3</span>
              <h4 className="text-xs sm:text-sm font-bold text-[var(--text)]">
                {isUa ? "Швидка підказка для невидимих соусів" : "Quick Note for Secret Dressings"}
              </h4>
            </div>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed pl-8.5">
              {isUa 
                ? "Якщо салат заправлений прихованим соусом на дні — просто скажи голосом або допиши 'з 1 ст.л. майонезу', і AI миттєво додасть ці калорії."
                : "If you have heavy dressing buried at the bottom, use the instant voice/text memo ('with 2 tbsp caesar dressing') and the AI recalculates automatically."}
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-zinc-100/80 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700/70 space-y-2 hover:border-emerald-500/40 dark:hover:border-emerald-500/50 transition-colors shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-emerald-500/20 dark:bg-emerald-500/25 border border-emerald-500/40 dark:border-emerald-500/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-xs font-black shrink-0">4</span>
              <h4 className="text-xs sm:text-sm font-bold text-[var(--text)]">
                {isUa ? "Штрихкод для фабричних снеків" : "Barcode & Label Scanner for Packaged Goods"}
              </h4>
            </div>
            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed pl-8.5">
              {isUa 
                ? "Для протеїнових батончиків, йогуртів та снеків у пачці — скануй штрихкод або таблицю складу для 100% відповідності заводським даним."
                : "For packaged protein bars, yogurts, and snacks, scanning the barcode or ingredient label yields 100% manufacturer-grade nutritional truth."}
            </p>
          </div>
        </div>
      </div>

      {/* --- FAQ SECTION --- */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-xl space-y-4 relative overflow-hidden",
        cardBorderClass
      )}>
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-80 h-80 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="text-center max-w-xl mx-auto space-y-1 relative z-10">
          <h2 className="text-xl sm:text-2xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Все, що ти хотів запитати" : "Common Questions & Answers"}
          </h2>
        </div>

        <div className="space-y-2.5 pt-2 relative z-10">
          {faqs.map((faq, idx) => {
            const isExpanded = expandedFaqIndex === idx;
            return (
              <div 
                key={idx}
                className={cn(
                  "rounded-2xl border overflow-hidden transition-all",
                  isExpanded
                    ? "border-emerald-500/40 dark:border-emerald-500/40 bg-zinc-100/80 dark:bg-zinc-800/70 shadow-md shadow-emerald-500/5 dark:shadow-emerald-950/20"
                    : "border-zinc-200 dark:border-zinc-800/90 bg-zinc-50/70 dark:bg-zinc-800/40 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-100/50 dark:hover:bg-zinc-800/60"
                )}
              >
                <button
                  onClick={() => {
                    sounds.playPop();
                    triggerHaptic('SELECTION');
                    setExpandedFaqIndex(isExpanded ? null : idx);
                  }}
                  className="w-full p-4 text-left flex items-center justify-between gap-3 cursor-pointer"
                >
                  <span className="text-xs sm:text-sm font-bold text-[var(--text)]">
                    {isUa ? faq.qUa : faq.qEn}
                  </span>
                  <ChevronDown className={cn("w-4 h-4 text-zinc-400 dark:text-zinc-500 transition-transform shrink-0", isExpanded && "rotate-180 text-emerald-500 dark:text-emerald-400")} />
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="px-4 pb-4 text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed border-t border-zinc-200/60 dark:border-zinc-700/60 pt-3"
                    >
                      {isUa ? faq.aUa : faq.aEn}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- BOTTOM CALL TO ACTION BANNER --- */}
      <div className={cn(
        "p-6 sm:p-8 rounded-3xl border shadow-2xl text-center space-y-4 relative overflow-hidden",
        isStalker
          ? "border-[#4ade80]/40 bg-black text-[#4ade80]"
          : isCyberpunk
            ? "border-[#00ffcc]/40 bg-[#0d0f18] text-[#00ffcc]"
            : isAnime
              ? "border-pink-500/30 bg-gradient-to-b from-pink-500/10 via-zinc-100/80 to-white dark:from-pink-950/40 dark:via-zinc-900/90 dark:to-zinc-950 text-[var(--text)]"
              : "border-emerald-500/20 dark:border-emerald-500/30 bg-gradient-to-b from-emerald-500/10 via-zinc-100/80 to-white dark:from-emerald-950/40 dark:via-zinc-900/90 dark:to-zinc-950 text-[var(--text)]"
      )}>
        {/* Energetic Radiant Aura & Floating Particles */}
        <div className="absolute inset-0 bg-tech-grid opacity-30 dark:opacity-20 pointer-events-none" />
        <motion.div 
          animate={{ scale: [1, 1.25, 1], opacity: [0.35, 0.65, 0.35] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-radial from-emerald-500/30 via-teal-500/15 to-transparent blur-3xl pointer-events-none"
        />
        <motion.div 
          animate={{ y: [0, -12, 0], x: [0, 8, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-4 right-12 w-48 h-48 bg-cyan-500/20 dark:bg-cyan-500/30 rounded-full blur-2xl pointer-events-none"
        />
        <motion.div 
          animate={{ y: [0, 12, 0], x: [0, -8, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-4 left-12 w-48 h-48 bg-emerald-500/20 dark:bg-emerald-500/30 rounded-full blur-2xl pointer-events-none"
        />
        <div className="relative z-10 w-14 h-14 rounded-full bg-emerald-500/20 dark:bg-emerald-500/25 border border-emerald-500/40 dark:border-emerald-500/50 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/20 dark:shadow-emerald-950/50">
          <Camera className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
        </div>

        <div className="space-y-1 max-w-md mx-auto relative z-10">
          <h3 className="text-xl sm:text-2xl font-black tracking-tight text-[var(--text)]">
            {isUa ? "Готовий перевірити це на своїй тарілці?" : "Ready to test it on your next meal?"}
          </h3>
          <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-300">
            {isUa 
              ? "Зроби фото свого обіду прямо зараз і отримай повний розклад за пару секунд."
              : "Snap a photo of whatever is in front of you and get your complete macro breakdown in seconds."}
          </p>
        </div>

        <button
          onClick={() => {
            sounds.playSuccess();
            triggerHaptic('SUCCESS');
            onStartScan();
          }}
          className={cn(
            "relative z-10 px-6 py-3.5 rounded-2xl text-xs sm:text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2 mx-auto transition-all shadow-xl hover:scale-105 active:scale-95 cursor-pointer",
            accentBgClass
          )}
        >
          <Sparkles className="w-4 h-4" />
          <span>{isUa ? "Запустити AI Сканер" : "Launch AI Scanner"}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
